/**
 * 销售订单详情页
 */
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Descriptions, Tag, Button, Space, Spin, Modal, Form, InputNumber, Input, Select, Table, message } from 'antd';
import { DollarOutlined, SendOutlined, CameraOutlined, UploadOutlined } from '@ant-design/icons';
import { salesApi } from '../../services/api';

const statusMap = { unpaid: '未收款', paid: '已收款', shipped: '已发货', installed: '已安装', completed: '已完成', cancelled: '已取消' };
const statusColors = { unpaid: 'orange', paid: 'blue', shipped: 'cyan', installed: 'processing', completed: 'success', cancelled: 'error' };

function SalesDetail() {
  const { id } = useParams();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shipModal, setShipModal] = useState(false);
  const [installModal, setInstallModal] = useState(false);
  const [payModal, setPayModal] = useState(false);
  const [form] = Form.useForm();
  const [payForm] = Form.useForm();

  const loadDetail = async () => {
    setLoading(true);
    try { const res = await salesApi.detail(id); setDetail(res.data); }
    catch (err) { message.error('加载详情失败'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadDetail(); }, [id]);

  // 收款确认（PC 收款登记：实收金额 + 收款方式，现金/当面扫码/转账/银行转账等）
  const handlePay = async (values) => {
    try {
      await salesApi.pay(id, values);
      message.success('收款确认成功');
      setPayModal(false);
      loadDetail();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || '收款失败，请重试';
      message.error(msg);
    }
  };

  const handleShip = async (values) => {
    try { await salesApi.ship(id, values); message.success('发货成功'); setShipModal(false); loadDetail(); } catch (err) { }
  };

  if (loading || !detail) return <div style={{ textAlign: 'center', padding: 100 }}><Spin size="large" /></div>;

  return (
    <div>
      <Card title={`销售单 ${detail.sale_no}`} extra={
        <Space>
          {detail.status === 'unpaid' && <Button type="primary" icon={<DollarOutlined />}
            onClick={() => { payForm.resetFields(); payForm.setFieldsValue({ amount: parseFloat(detail.amount || 0), pay_method: 'cash' }); setPayModal(true); }}>收款确认</Button>}
          {detail.status === 'paid' && <Button type="primary" onClick={() => { form.resetFields(); setShipModal(true); }} icon={<SendOutlined />}>发货</Button>}
          {(detail.status === 'paid' || detail.status === 'shipped') && detail.need_install === 1 &&
            <Button onClick={() => { form.resetFields(); setInstallModal(true); }} icon={<CameraOutlined />}>上传安装信息</Button>}
        </Space>
      }>
        <Descriptions bordered column={2} size="small">
          <Descriptions.Item label="单号">{detail.sale_no}</Descriptions.Item>
          <Descriptions.Item label="状态"><Tag color={statusColors[detail.status]}>{statusMap[detail.status]}</Tag></Descriptions.Item>
          <Descriptions.Item label="客户">{detail.customer_name}</Descriptions.Item>
          <Descriptions.Item label="电话">{detail.phone}</Descriptions.Item>
          <Descriptions.Item label="地址" span={2}>{detail.address}</Descriptions.Item>
          <Descriptions.Item label="联系人">{detail.contact_person}</Descriptions.Item>
          <Descriptions.Item label="SN/ID号">{detail.sn_number || '无'}</Descriptions.Item>
          <Descriptions.Item label="产品">{detail.product_name || '-'}</Descriptions.Item>
          <Descriptions.Item label="型号">{detail.product_model || '-'}</Descriptions.Item>
          <Descriptions.Item label="金额">¥{parseFloat(detail.amount).toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="数量">{detail.quantity}</Descriptions.Item>
          {detail.need_install === 1 && <>
            <Descriptions.Item label="保修开始">{detail.warranty_start || '-'}</Descriptions.Item>
            <Descriptions.Item label="保修结束">{detail.warranty_end || '-'}</Descriptions.Item>
            <Descriptions.Item label="保修期">{detail.warranty_months}个月</Descriptions.Item>
            <Descriptions.Item label="安装人员">{detail.technician_name || '-'}</Descriptions.Item>
          </>}
          {detail.express_no && <>
            <Descriptions.Item label="快递公司">{detail.express_company}</Descriptions.Item>
            <Descriptions.Item label="快递单号">{detail.express_no}</Descriptions.Item>
          </>}
        </Descriptions>
      </Card>

      {detail.install_photo && (
        <Card title="安装照片" size="small" style={{ marginTop: 16 }}>
          <img src={detail.install_photo} alt="安装照片" style={{ maxWidth: 400 }} />
        </Card>
      )}

      {/* 收款登记弹窗 */}
      <Modal title="收款确认" open={payModal} onCancel={() => setPayModal(false)} onOk={() => payForm.submit()} okText="确认收款">
        <div style={{ marginBottom: 12, padding: '8px 12px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 4, fontSize: 13, color: '#389e0d' }}>
          该销售订单金额 <b>¥{parseFloat(detail.amount || 0).toFixed(2)}</b>。登记实际收款方式入账后，订单进入"已收款"，方可发货。
        </div>
        <Form form={payForm} layout="vertical" onFinish={handlePay}>
          <Form.Item name="amount" label="实收金额" rules={[{ required: true, message: '请输入实收金额' }]} extra="默认等于订单金额，如抹零可修改">
            <InputNumber prefix="¥" min={0.01} precision={2} style={{ width: '100%' }} placeholder="请输入实收金额" />
          </Form.Item>
          <Form.Item name="pay_method" label="收款方式" rules={[{ required: true, message: '请选择收款方式' }]}>
            <Select
              placeholder="请选择收款方式"
              options={[
                { value: 'cash', label: '现金' },
                { value: 'scan', label: '当面扫码（微信/支付宝收款码）' },
                { value: 'transfer', label: '转账（微信/支付宝转账）' },
                { value: 'bank', label: '银行转账' },
                { value: 'other', label: '其他' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 发货弹窗 */}
      <Modal title="发货" open={shipModal} onCancel={() => setShipModal(false)} onOk={() => form.submit()}>
        <Form form={form} layout="vertical" onFinish={handleShip}>
          <Form.Item name="express_company" label="快递公司" initialValue="京东快递"><Input /></Form.Item>
          <Form.Item name="express_no" label="快递单号"><Input placeholder="系统可自动获取京东运单号" /></Form.Item>
        </Form>
      </Modal>

      {(detail.payRecords && detail.payRecords.length > 0) && (
        <Card title="收款记录" size="small" style={{ marginTop: 16 }}>
          <Table
            rowKey="id"
            size="small"
            pagination={false}
            dataSource={detail.payRecords}
            columns={[
              { title: '金额', dataIndex: 'amount', width: 110, render: (v) => `¥${parseFloat(v).toFixed(2)}` },
              { title: '方式', dataIndex: 'pay_method', width: 120, render: (v) => payMethodText[v] || v },
              { title: '支付单号', dataIndex: 'transaction_id', ellipsis: true },
              { title: '时间', dataIndex: 'created_at', width: 170, render: (v) => v ? new Date(v).toLocaleString('zh-CN') : '-' },
            ]}
          />
        </Card>
      )}
    </div>
  );
}

export default SalesDetail;
